import { useSyncExternalStore } from "react";
import { Link } from "react-router-dom";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { useBrandHomeRoute } from "@/hooks/useBrandHomeRoute";
import { useColorTheme } from "@/hooks/useColorTheme";
import {
  getAuthSessionSnapshot,
  getContactFirstName,
  subscribeAuthSession,
} from "@/lib/auth/session";
import { useSandboxCredentialsEnabled } from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import { getGlofoxDocsHomeHref } from "@/lib/portal/docs-urls";
import { SAME_ORIGIN_NEW_TAB_REL } from "@/lib/portal/same-origin-new-tab";
import {
  PORTAL_HOME_ROUTE,
  PORTAL_LOGIN_LEGACY_ROUTE,
  PORTAL_SIGNUP_LEGACY_ROUTE,
  PORTAL_SIGNUP_ROUTE,
} from "@/lib/portal/portal-routes";
import { UserAccountMenu } from "./UserAccountMenu";
import { SkipToContent } from "./SkipToContent";

export function HomeTopNav() {
  const sandboxCredentialsEnabled = useSandboxCredentialsEnabled();
  const brandHomeRoute = useBrandHomeRoute();
  // The legacy experience has no dark styling, so dark mode is ignored there.
  const { theme, setTheme, darkModeEnabled } = useColorTheme({
    darkModeEnabled: sandboxCredentialsEnabled,
  });
  const session = useSyncExternalStore(
    subscribeAuthSession,
    getAuthSessionSnapshot,
    getAuthSessionSnapshot,
  );
  const displayName = session
    ? getContactFirstName(session.user.contact_name)
    : null;

  return (
    <>
      <SkipToContent />
      <nav className="topnav topnav--dark" data-testid="home-nav">
        <div className="topnav__brand-group">
          <Link
            className="topnav-logo"
            to={brandHomeRoute}
            data-testid="home-nav-logo"
          >
            <AbcFitnessBrandLogo to="" theme="dark" />
          </Link>
          <span className="topnav__sep" aria-hidden>
            /
          </span>
          <span className="topnav__product">Developer Portal</span>
        </div>

        <div className="nav-right">
          {darkModeEnabled ? (
            <label className="theme-toggle" title="Toggle color theme">
              <input
                type="checkbox"
                role="switch"
                checked={theme === "dark"}
                aria-label="Dark mode"
                data-testid="home-nav-theme-toggle"
                onChange={(event) =>
                  setTheme(event.target.checked ? "dark" : "light")
                }
              />
              <span aria-hidden>
                <i className={`ti ${theme === "dark" ? "ti-sun" : "ti-moon"}`} />
              </span>
            </label>
          ) : null}
          {sandboxCredentialsEnabled ? (
            <a
              href={getGlofoxDocsHomeHref()}
              className="btn btn-outline"
              target="_blank"
              rel={SAME_ORIGIN_NEW_TAB_REL}
              data-testid="home-nav-docs"
            >
              Docs
              <i className="ti ti-external-link" aria-hidden />
            </a>
          ) : null}
          {displayName ? (
            <UserAccountMenu userName={displayName} testIdPrefix="home-nav" />
          ) : (
            <>
              <Link
                to={
                  sandboxCredentialsEnabled
                    ? PORTAL_HOME_ROUTE
                    : PORTAL_LOGIN_LEGACY_ROUTE
                }
                className="btn btn-outline"
                data-testid="home-nav-login"
              >
                <i className="ti ti-login" aria-hidden /> Log in
              </Link>
              <Link
                to={
                  sandboxCredentialsEnabled
                    ? PORTAL_SIGNUP_ROUTE
                    : PORTAL_SIGNUP_LEGACY_ROUTE
                }
                className="btn btn-outline"
                data-testid="home-nav-signup"
              >
                <i className="ti ti-user-plus" aria-hidden /> Sign up
              </Link>
            </>
          )}
        </div>
      </nav>
    </>
  );
}
